import {
    useState,
    useEffect,
    useMemo
} from 'react';
import Select, { components } from 'react-select';
import Switch from "react-switch";
import { HexColorInput } from "react-colorful";
import { PopoverPicker } from '../ColorPicker/PopoverPicker';
import { getContentPlaylists } from './utils';
import pauseIcon from '../../images/icons/screens/pause.svg';
import playlistIcon from '../../images/icons/screens/playlist.svg';
import globeIcon from '../../images/icons/screens/globe.svg';
import oneViewIcon from '../../images/icons/screens/one-view.svg';
import downloadIcon from '../../images/icons/screens/download.svg';
import actionfigureQr from '../../images/icons/screens/actionfigure-qr.svg';
import dropdownOptions from './dropdownOptions.json';

import MapWrapper from './MapWrapper';

const svgToPng = (svgString, width, height) => {
    return new Promise((resolve) => {
        const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);

        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
            URL.revokeObjectURL(url);
            resolve(canvas.toDataURL('image/png'));
        };
        img.src = url;
    });
};

const ScreenConfiguration = ({
    state,
    setState,
    handleInputChange,
    handleMultiInputChanges,
    customerId,
    selectedProduct,
    locations,
    oneViewOptions,
    allPermissions,
    user
}) => {

    const playlist = {...state?.playlist};

    const [contentPlaylists, setContentPlaylists] = useState([]);
    const [locationOptions, setLocationOptions] = useState([]);
    const [pendingRotation, setPendingRotation] = useState(null);

    const [pngUrl, setPngUrl] = useState(null);

    const filteredContentPlaylists = useMemo(
        () => contentPlaylists.filter(pl => pl.deviceType === playlist.deviceType),
        [contentPlaylists, playlist.deviceType, playlist.rotation]
    );

    let purchasedProducts = useMemo(() => {
        let products = [];
        if (playlist.location) {
            for (const [product, purchased] of Object.entries(playlist.location.purchasedProducts)) {
                if (purchased) {
                    products.push(product);
                }
            }
        }

        return products;
    }, [playlist]);

    let allowEdit = useMemo(() => {
        return playlist?.createdAt
            && 'screen' in allPermissions
            && playlist?.location?.id in allPermissions['screen']
            && allPermissions['screen'][playlist.location.id].includes('editor')
    }, [playlist?.createdAt]);

    /* Render the little icons in the Orientation and One View (default) dropdowns */
    // https://stackoverflow.com/a/60965985/29499558
    const { Option, SingleValue } = components;
    let defaultOptionStyle = {display: "flex", flexDirection: "row", alignItems: "center" };
    let imgStyle = { width: 20, marginRight: "0.5em" };
    const OrientationIconOption = props => (
        <Option {...props}>
            <div style={defaultOptionStyle}>
                <img
                    src={require('../../images/icons/screens/' + props.data.icon).default}
                    style={imgStyle}
                    alt={props.data.label}
                />
                {props.data.label}
            </div>
        </Option>
    );

    // Had to use ChatGPT to find this one out, so no link
    // This will style the default option.
    // The default option is still determined by the <Select> defaultValue attribute.
    const DefaultSingleValue = (props) => {
        let optionStyle = {...defaultOptionStyle};
        let optionImgStyle = {...imgStyle};
        if (props.data.isDisabled || props.selectProps.isDisabled) {
            optionStyle.color = "#919191";
            optionImgStyle.opacity = 0.35;
        }

        return <SingleValue {...props}>
            <div style={optionStyle}>
                <img
                  src={require('../../images/icons/screens/' + props.data.icon).default}
                  style={optionImgStyle}
                  alt={props.data.label}
                />
                {props.data.label}
            </div>
        </SingleValue>
    }

    // Default value of the One View dropdown is based on what the customer has purchased.
    let defaultOption = oneViewOptions.find((option) => !option.isDisabled);

    // Render the One View dropdown icons
    const OneViewIconOption = props => {
        let optionProps = {...props};
        let optionImgStyle = {...imgStyle};

        if (optionProps.data.isDisabled) {
            // The component will modify the label when option is disabled.
            optionProps["isDisabled"] = true;
            // But we must modify the img tag ourselves.
            optionImgStyle["opacity"] = 0.35;
        }

        let oneViewOption = <Option {...optionProps}>
            <div style={defaultOptionStyle}>
                <img
                    src={require('../../images/icons/screens/' + props.data.icon).default}
                    style={optionImgStyle}
                    alt={props.data.label}
                />
                {props.data.label}
            </div>
        </Option>

        return oneViewOption;
    };
    /* End Render */

    /* React-Select custom styling */
    const basicConfigDropdownStyling = {
        container: (baseStyles, state) => ({
          ...baseStyles,
          // Too much gray if the dropdown is disabled.
          boxShadow: state.isDisabled ? "unset" : "0px 2px 8px 4px rgba(0, 0, 0, 0.09)",
          borderRadius: "6px",
          borderColor: "none"
        }),
        control: (baseStyles, state) => ({
            ...baseStyles,
            border: "none",
            cursor: oneViewOptions.filter((option) => !option.isDisabled).length === 0 ? "default" : "pointer",
            borderRadius: "6px",
            borderColor: "none"
        }),
        indicatorSeparator: (baseStyles, state) => ({
            ...baseStyles,
            display: "none"
        }),
        option: (baseStyles, state) => ({
            ...baseStyles,
            color: state.isDisabled ? "#919191" : "inherit",
            cursor: "pointer"
        })
    };


    useEffect(() => {
        if (playlist?.qrCodes?.mobile?.qr) {
            svgToPng(playlist.qrCodes.mobile.qr, 100, 100).then(setPngUrl);
        } else {
            setPngUrl(null);
        }
    }, [playlist?.qrCodes?.mobile?.qr]);



    useEffect(() => {
        getContentPlaylists(customerId)
        .then(data => {
            setContentPlaylists(data);
        })
    }, []);

    useEffect(() => {
        let tempLocations = [];

        // Location IDs with 'screen' permissions
        let locationIdsWithScreenPermissions = 'screen' in allPermissions
            ? Object.keys(allPermissions['screen'])
            : [];

        locations?.forEach(location => {
            if (locationIdsWithScreenPermissions.includes(location.id)) {
                tempLocations.push({label: location.name, value: location.id});
            }
        })
        setLocationOptions(tempLocations);
    }, [locations]);

    const handleRadioSelect = (attribute, radioValue) => {
        if (attribute === 'screenType') {
            if (radioValue === 'oneView') {
                handleMultiInputChanges(['screenType', 'contentShortCode', 'otherSlideUrl'], [defaultOption?.value, null, null]);
            }
            //Transportation radio button clicked
            else if (radioValue === 'standby') {
                handleMultiInputChanges(['screenType', 'contentShortCode', 'otherSlideUrl'], [radioValue, null, null]);
            }
            else if (radioValue === 'contentPlaylist') {
                handleMultiInputChanges(['screenType', 'otherSlideUrl'], [radioValue, null]);
            }
            else if (radioValue === 'other') {
                handleMultiInputChanges(['screenType', 'contentShortCode'], [radioValue, null]);
            }
        } else if (attribute === 'messagingShortCode') {
            handleMultiInputChanges(['messagingShortCode'], [radioValue]);
        }
    }

    const inScreenMessagingEnabled = () => {
        let enabled = false;
        if (purchasedProducts.includes("messaging")
            && selectedProduct === 'screen_pro') {
            enabled = true;
        }

        return enabled;
    }

    const isStandbyByDefault = () => {
        let otherOptionsSelected = playlist?.screenType && ['contentPlaylist', 'other'].includes(playlist?.screenType);
        return (oneViewOptions.filter((option) => !option.isDisabled).length === 0 && !otherOptionsSelected);
    }

    // const handleColorChange = (property, value) => {
    //     //Making sure value does not come in with a # at the start
    //     const color = value.length > 6 ? value.slice(1) : value;
    //     const tempConfigs = {...state.playlist.brandingConfigs};
    //     tempConfigs[property] = color;

    //     // If value is the same as the default, remove.
    //     if ('#' + defaultConfigs[property].toLowerCase() === color.toLowerCase()) {
    //         delete tempConfigs[property];
    //     }

    //     setState({
    //         ...state,
    //         playlist: {...state.playlist, brandingConfigs: tempConfigs}
    //     });
    //     setStateChanged(true);
    // }

    const applyOrientationChange = (rotation, clearContentShortCode) => {
        let propNames = ['rotation', 'deviceType'];
        let newValues = [rotation, (rotation === '0' ? 'landscape' : 'portrait')];
        if (clearContentShortCode) {
            propNames.push('contentShortCode');
            newValues.push(null);
        }
        handleMultiInputChanges(propNames, newValues);
    }

    const handleOrientationChange = (rotation) => {
        const newDeviceType = rotation === '0' ? 'landscape' : 'portrait';
        const contentPlaylist = contentPlaylists.find(pl => pl.shortCode === playlist.contentShortCode);

        if (contentPlaylist && contentPlaylist.deviceType !== newDeviceType) {
            setPendingRotation(rotation);
        } else {
            applyOrientationChange(rotation, false);
        }
    }

    const havePlaylistFullPermission = () => {
        return 'playlistFull' in allPermissions
            && playlist?.location?.id in allPermissions['playlistFull']
            && allPermissions['playlistFull'][playlist.location.id].includes('editor');
    }

    let haveContentManagementPermission = () => {
        return 'contentManagement' in allPermissions
            && playlist?.location?.id in allPermissions['contentManagement']
            && allPermissions['contentManagement'][playlist.location.id].includes('editor');
    };

    const handleDownload = () => {
        const a = document.createElement('a');
        a.href = pngUrl;
        a.download = playlist.label.replace(" ", "_") + '-qr-code.png';
        a.click();
    };

 
    return (
        <>
        <div className="ScreenConfigurationPage">
            <div className="ScreenConfiguration">
                <h2>Configuration</h2>
                <div className="basic-configuration">
                    <div className="left-col">
                        <h3>Presentation and distribution</h3>
                        <div>
                            <div className="device-option">
                                <div className="device-option-radio-button">
                                    <input
                                        type="radio"
                                        name="device"
                                        value="landscape"
                                        onChange={(e) => handleMultiInputChanges(['deviceType', 'contentShortCode'], [e.target.value, null])}
                                        disabled={!allowEdit}
                                        style={!allowEdit ? {"cursor": "default"} : {}}
                                        checked={playlist?.deviceType === "landscape" || playlist?.deviceType === "portrait"}
                                    />
                                </div>
                                <div className="device-option__details">
                                    <div className="proxima-bold">Actionfigure&reg; Screen for presentation on fixed displays</div>
                                    <div className="device-option__description">Actionfigure Screen is designed for fixed, high resolution displays (wall mounted TVs, projectors, and large format).</div>
                                    <div className="screen-orientation">
                                        <div className="device-option__label__orientation">Display orientation:</div>
                                        <div style={{flexGrow: 1}}>
                                            <Select
                                                onChange={(e) => handleOrientationChange(e.value)}
                                                defaultValue={dropdownOptions.SCREEN_ORIENTATION[0]}
                                                options={dropdownOptions.SCREEN_ORIENTATION}
                                                value={dropdownOptions.SCREEN_ORIENTATION.find(option => option.value === playlist.rotation)}
                                                components={{ Option: OrientationIconOption, SingleValue: DefaultSingleValue }}
                                                styles={basicConfigDropdownStyling}
                                                isDisabled={!allowEdit || (playlist?.deviceType !== "landscape" && playlist?.deviceType !== "portrait")}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="device-option">
                                <div className="device-option-radio-button">
                                    <input
                                        type="radio"
                                        name="device"
                                        value="mobile"
                                        onChange={(e) => handleMultiInputChanges(['deviceType', 'contentShortCode'], [e.target.value, null])}
                                        disabled={!allowEdit}
                                        style={!allowEdit ? {"cursor": "default"} : {}}
                                        checked={playlist?.deviceType === "mobile"}
                                    />
                                </div>
                                <div className="device-option__details">
                                    <div className="proxima-bold">Actionfigure&reg; Mobile for distribution directly to mobile and tablet devices</div>

                                    <div className="device-option__mobile">
                                        <div className="device-option__description">Used primarily for distribution on mobile devices such as phones and tablets. The QR code can be downloaded and distributed. When scanned, the user's phone will download and automatically activate the Actionfigure app.</div>
                                        <div className="qr-download">
                                            {
                                                pngUrl
                                                ? <img className="qr" src={pngUrl} alt="QR code" />
                                                : <img className="qr qr-placeholder" src={actionfigureQr} />
                                            }
                                            <button onClick={pngUrl ? handleDownload : null} className="qr-download" style={pngUrl ? {cursor: "pointer"} : {cursor: "default", filter: "grayscale(100%) brightness(150%)"}}><img src={downloadIcon} /></button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="right-col">
                        <h3>General settings</h3>
                        <div className="form-and-map">
                            <div className="basic-settings-form">
                                <div className="input-container">
                                    <div className="basic-settings__label">Name:</div>
                                    <div className="basic-settings__value">
                                        <input
                                            type="text"
                                            className="input-field"
                                            value={playlist.label || ''}
                                            onChange={(e) => handleInputChange('label', e.target.value)}
                                            disabled={!allowEdit}
                                            style={!allowEdit ? {"cursor": "default", "color": "#919191", "border": "none"} : { "border": "none" }}
                                        />
                                    </div>
                                </div>
                                <div className="input-container">
                                    <div className="basic-settings__label">Location:</div>
                                    <div className="basic-settings__value">
                                        <Select
                                            onChange={(option) => handleInputChange('locationId', option.value)}
                                            options={locationOptions}
                                            value={ locationOptions.find(location => location.value === playlist?.locationId) }
                                            styles={basicConfigDropdownStyling}
                                            isDisabled={!allowEdit}
                                        />
                                    </div>
                                </div>
                                <div className="input-container">
                                    <div className="basic-settings__label">Language:</div>
                                    <div className="basic-settings__value">
                                        <Select
                                            onChange={(option) => handleInputChange('language', option.value)}
                                            options={ dropdownOptions.LANGUAGE_OPTIONS }
                                            defaultValue={ dropdownOptions.LANGUAGE_OPTIONS[0] }
                                            value={ dropdownOptions.LANGUAGE_OPTIONS.find(lang => lang.value === playlist?.language) }
                                            styles={basicConfigDropdownStyling}
                                            isDisabled={!allowEdit}
                                        />
                                    </div>
                                </div>
                                <div className="input-container">
                                    <div className="basic-settings__label">Measurements:</div>
                                    <div className="basic-settings__value">
                                        <Select
                                            onChange={(option) => handleInputChange('measurement', option.value)}
                                            options={dropdownOptions.MEASUREMENTS}
                                            defaultValue={ dropdownOptions.MEASUREMENTS[0] }
                                            value={dropdownOptions.MEASUREMENTS.find((option) => option.value === playlist?.measurement)}
                                            styles={basicConfigDropdownStyling}
                                            isDisabled={!allowEdit}
                                        />
                                    </div>
                                </div>
                                <div className="input-container">
                                    <div className="basic-settings__label">{/* Placeholder */}</div>
                                    <div className="basic-settings__value support-blurb">
                                        Need to add, remove, or update a location? <br/> Contact support@actionfigure.ai
                                    </div>
                                </div>
                            </div>
                            <div className="map-container">
                                <MapWrapper state={state} setState={setState} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="ScreenTypeConfiguration">
                <div className="content-management-settings">
                    <div className="cm-setting">
                        <h2>Full-Screen content management</h2>
                        <h3>What people should be seeing</h3>
                        <p>Specify what content you would like to be showing full-screen. Be sure to save any changes.</p>
                        <div className="screen-type-option-wrapper">
                            <div className="screen-type-container">
                                <div className="radio-button">
                                    <input
                                        type="radio"
                                        checked={oneViewOptions.some((option) => option.value === playlist.screenType)}
                                        onChange={() => handleRadioSelect('screenType', 'oneView')}
                                        disabled={!allowEdit || oneViewOptions.filter((option) => !option.isDisabled).length === 0}
                                        style={!allowEdit || oneViewOptions.filter((option) => !option.isDisabled).length === 0 ? {"cursor": "default"} : {}}
                                    />
                                </div>
                                <div className="screen-type-icon">
                                    <img src={oneViewIcon} />
                                </div>
                                <div className="radio-label">
                                    <div className="label-and-dropdown">
                                        <div className="label"> One view: (default) </div>
                                        <div className="screen-type-dropdown">
                                            <Select
                                                onChange={(e) => handleInputChange('screenType', e.value)}
                                                defaultValue={defaultOption}
                                                options={oneViewOptions}
                                                value={dropdownOptions.VIEWS.find(option => option.value === playlist.screenType)}
                                                components={{ Option: OneViewIconOption, SingleValue: DefaultSingleValue }}
                                                styles={basicConfigDropdownStyling}
                                                isDisabled={!allowEdit || oneViewOptions.filter((option) => !option.isDisabled).length === 0}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="screen-type-description">Present only the contents of one view you specify.</div>
                        </div>
                        <div className="screen-type-option-wrapper">
                            <div className={`screen-type-container ${ playlist?.location?.id && !havePlaylistFullPermission() ? "disabled-label" : ''}` }>
                                <div className="radio-button">
                                    <input
                                        type="radio"
                                        checked={playlist.screenType === 'contentPlaylist'}
                                        onChange={() => handleRadioSelect('screenType', 'contentPlaylist')}
                                        disabled={!allowEdit || !havePlaylistFullPermission()}
                                        style={!allowEdit || !havePlaylistFullPermission() ? {"cursor": "default"} : {}}
                                    />
                                </div>
                                <div  className="screen-type-icon">
                                    <img className={playlist?.location?.id && !havePlaylistFullPermission() ? 'disabled': ''} src={playlistIcon} />
                                </div>
                                <div className="radio-label">
                                    <div className="label-and-dropdown">
                                        <div className="label">Playlist:</div>
                                        <div className="screen-type-dropdown">
                                            <Select
                                                onChange={(option) => handleMultiInputChanges(['contentShortCode', 'screenType', 'otherSlideUrl'], [option.value, 'contentPlaylist', null])}
                                                options={filteredContentPlaylists.map(pl => {
                                                    return {
                                                        label: pl.label,
                                                        value: pl.shortCode
                                                    }
                                                })}
                                                value={
                                                    playlist.contentShortCode
                                                    ? filteredContentPlaylists.find(pl => pl.shortCode === playlist.contentShortCode)
                                                    : null
                                                }
                                                styles={basicConfigDropdownStyling}
                                                menuShouldScrollIntoView={false}
                                                isDisabled={!allowEdit || !havePlaylistFullPermission()}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="screen-type-description">Choose from any of your playlists.</div>
                        </div>
                        <div className="screen-type-option-wrapper">
                            <div className={`screen-type-container ${ playlist?.location?.id && !haveContentManagementPermission() ? "disabled-label" : '' }`}>
                                <div className="radio-button">
                                    <input
                                        type="radio"
                                        checked={playlist.screenType === 'other'}
                                        onChange={() => handleRadioSelect('screenType', 'other')}
                                        disabled={!allowEdit || !haveContentManagementPermission()}
                                        style={!allowEdit || !haveContentManagementPermission() ? {"cursor": "default"} : {}}
                                    />
                                </div>
                                <div  className="screen-type-icon">
                                    <img className= {playlist?.location?.id && !haveContentManagementPermission() ? 'disabled': ''} src={globeIcon} />
                                </div>
                                <div className="radio-label">
                                    <div className="label-and-dropdown">
                                        <div className="label">Website:</div>
                                        <div className="screen-type-input">
                                            <input
                                                type="text"
                                                style={{background: '#fff', width: '100%', cursor: !allowEdit || !haveContentManagementPermission() ? "default" : "text"}}
                                                value={playlist?.otherSlideUrl || ''}
                                                onChange={(e) => handleMultiInputChanges(['screenType', 'otherSlideUrl', 'contentShortCode'], ['other', e.target.value, null])}
                                                className="input-field"
                                                placeholder='https://...'
                                                disabled={!allowEdit || !haveContentManagementPermission()}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="screen-type-description">Show the contents of a 3rd party URL. Note: Some 3rd party websites will not allow their websites to be shown in this manner.</div>
                        </div>
                        <div className="screen-type-option-wrapper">
                            <div className="screen-type-container">
                                <div className="radio-button">
                                    <input
                                        type="radio"
                                        checked={isStandbyByDefault() || playlist.screenType === 'standby'}
                                        onChange={() => handleRadioSelect('screenType', 'standby')}
                                        disabled={!allowEdit}
                                        style={!allowEdit ? {"cursor": "default"} : {}}
                                    />
                                </div>
                                <div className="screen-type-icon"><img src={pauseIcon} /></div>
                                <div className="radio-label">
                                    <div className="label-and-dropdown">
                                        <div className="label">Standby</div>
                                    </div>
                                </div>
                            </div>
                            <div className="screen-type-description">Turns off all content and shows a "Content not available" message.</div>
                        </div>
                    </div>
                    <div className="cm-setting">
                        <h2>In-Screen content management</h2>
                        <h3>Messaging</h3>
                        <p>For Screen Pro customers with In-Screen Messaging, please specify what playlist should be shown on this Screen.</p>
                        <div className="screen-type-option-wrapper">
                            <div className="screen-type-container">
                                <div className="radio-button">
                                    <input
                                        type="radio"
                                        checked={playlist.messagingShortCode === null}
                                        onChange={() => handleRadioSelect('messagingShortCode', null)}
                                        disabled={!inScreenMessagingEnabled() || !allowEdit}
                                        style={!allowEdit ? {"cursor": "default"} : {}}
                                    />
                                </div>
                                <div className="screen-type-icon"><img className={!inScreenMessagingEnabled() ? "disabled" : ''} src={pauseIcon} /></div>
                                <div className={`radio-label ${!inScreenMessagingEnabled() ? "disabled-label": ''}`}>
                                    <div className="label-and-dropdown">
                                        <div className="label">Standby (default)</div>
                                    </div>
                                </div>
                            </div>
                            <div className="screen-type-description">
                                Turns off all content and shows a "Content not available" message.
                            </div>
                        </div>
                        <div className="screen-type-option-wrapper">
                            <div className="screen-type-container">
                                <div className="radio-button">
                                    <input
                                        type="radio"
                                        checked={playlist.messagingShortCode !== null && selectedProduct === 'screen_pro'}
                                        onChange={() => handleRadioSelect('messagingShortCode', '')}
                                        disabled={!inScreenMessagingEnabled() || !allowEdit}
                                        style={!allowEdit ? {"cursor": "default"} : {}}
                                    />
                                </div>
                                <div  className="screen-type-icon"><img className={!inScreenMessagingEnabled() ? "disabled" : ''} src={playlistIcon} /></div>
                                <div className={`radio-label ${!inScreenMessagingEnabled() ? "disabled-label": ''}`}>
                                    <div className="label-and-dropdown">
                                        <div className="label">Playlist:</div>
                                        <div className="screen-type-dropdown">
                                            <Select
                                                onChange={(option) => handleMultiInputChanges(['messagingShortCode'], [option.value])}
                                                options={contentPlaylists.map(pl => {
                                                    return {
                                                        label: pl.label,
                                                        value: pl.shortCode
                                                    }
                                                })}
                                                className="playlist-dropdown"
                                                value={contentPlaylists.find(pl => pl.shortCode === playlist.messagingShortCode)}
                                                styles={basicConfigDropdownStyling}
                                                menuShouldScrollIntoView={false}
                                                isDisabled={!inScreenMessagingEnabled() || !allowEdit}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="screen-type-description">Present only the contents of one view you specify.</div>
                        </div>
                        <h3>Messaging bar</h3>
                        <p>A multi-purpose content area at the bottom of your screen.</p>
                        <div className="screen-type-option-wrapper">
                            <div className="input-container">
                                <div className="basic-settings__label">Show:</div>
                                <div className="basic-settings__value">
                                    <Switch
                                        className="switch"
                                        checked={playlist?.showAlertBar || false}
                                        onChange={(checked) => handleMultiInputChanges(['showAlertBar', 'alertTextBackground', 'alertTextColor'], [checked, checked ? '#FFFFFF' : playlist?.alertTextBackground, checked ? '#000000' : playlist?.alertTextColor])}
                                        onColor="#2386EE"
                                        uncheckedIcon
                                        checkedIcon={false}
                                        disabled={!allowEdit}
                                    />
                                </div>
                            </div>
                            <div className="input-container">
                                <div className="basic-settings__label">Message:</div>
                                <div className="basic-settings__value">
                                    <input type="text" className={`input-field ${!allowEdit || !playlist?.showAlertBar ? "disabled" : ""}`} value={playlist.alertText || ''} onChange={(e) => handleMultiInputChanges(['alertText', 'showAlertBar'], [e.target.value, true])} disabled={!allowEdit || !playlist?.showAlertBar} />
                                </div>
                            </div>
                            <div className="input-container">
                                <div className="basic-settings__label">Background:</div>
                                <HexColorInput
                                    className={`input-color ${!allowEdit || !playlist?.showAlertBar ? "disabled" : ""}`}
                                    color={playlist?.alertTextBackground || '#FFFFFF'}
                                    onChange={(color) => handleInputChange('alertTextBackground', color.length > 6 ? color.slice(1) : color)}
                                    prefixed={true}
                                    alpha={true}
                                    disabled={!allowEdit || !playlist?.showAlertBar}
                                />
                                <PopoverPicker
                                    color={
                                        playlist?.alertTextBackground
                                            ? (playlist.alertTextBackground.startsWith('#')
                                                ? playlist.alertTextBackground
                                                : '#' + playlist.alertTextBackground)
                                            : '#FFFFFF'
                                    }
                                    onChange={(color) => handleInputChange('alertTextBackground', color.length > 6 ? color.slice(1) : color)}
                                    disabled={!allowEdit || !playlist?.showAlertBar}
                                    alpha={true}
                                />
                            </div>
                            <div className="input-container">
                                <div className="basic-settings__label">Text:</div>
                                <HexColorInput
                                    className={`input-color ${!allowEdit || !playlist?.showAlertBar ? "disabled" : ""}`}
                                    color={playlist?.alertTextColor || '#000000'}
                                    onChange={(color) => handleInputChange('alertTextColor', color.length > 6 ? color.slice(1) : color)}
                                    prefixed={true}
                                    alpha={true}
                                    disabled={!allowEdit || !playlist?.showAlertBar}
                                />
                                <PopoverPicker
                                    color={
                                        playlist?.alertTextColor
                                            ? (playlist.alertTextColor.startsWith('#')
                                                ? playlist.alertTextColor
                                                : '#' + playlist.alertTextColor)
                                            : '#000000'
                                    }
                                    onChange={(color) => handleInputChange('alertTextColor', color.length > 6 ? color.slice(1) : color)}
                                    disabled={!allowEdit || !playlist?.showAlertBar}
                                    alpha={true}
                                />
                            </div>
                            <div className="input-container">
                                <div className="basic-settings__label">Scroll speed:</div>
                                 <Select
                                    onChange={(option) => handleInputChange('alertTextScrollSpeed', option.value)}
                                    options={[{
                                        label: 'Slow', value: 100
                                    }, {
                                        label: 'Medium (default)', value: 200
                                    }, {
                                        label: 'Fast', value: 300
                                    }]}
                                    className={`dropdown ${!allowEdit || !playlist?.showAlertBar ? "disabled" : ""}`}
                                    value={[
                                        { label: 'Slow', value: 100 },
                                        { label: 'Medium (default)', value: 200 },
                                        { label: 'Fast', value: 300 }
                                    ].find(opt => opt.value == playlist?.alertTextScrollSpeed)}
                                    styles={basicConfigDropdownStyling}
                                    menuShouldScrollIntoView={false}
                                    isDisabled={!allowEdit || !playlist?.showAlertBar}
                                    menuPlacement="top"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        {pendingRotation !== null && (
            <OrientationMismatchModal
                onConfirm={() => {
                    applyOrientationChange(pendingRotation, true);
                    setPendingRotation(null);
                }}
                onCancel={() => setPendingRotation(null)}
            />
        )}
        </>
    );
}

const OrientationMismatchModal = ({ onConfirm, onCancel }) => {
    useEffect(() => {
        document.body.classList.add("no-scroll");
        return () => document.body.classList.remove("no-scroll");
    }, []);

    return (
        <div className="modal-wrapper">
            <div className="OrientationMismatchModal modal">
                <p>The playlist currently being displayed on this Screen is not designed to work with the new orientation. You will need to select a compatible playlist if you proceed.</p>
                <div className="bottom-container">
                    <button className="button cancel-button" onClick={onCancel}>Cancel</button>
                    <button className="button modal-button" onClick={onConfirm}>Ok</button>
                </div>
            </div>
        </div>
    );
}

export default ScreenConfiguration;
